import type { ISchedule, IScheduleBlock } from '@/types/schedule';
import type { IWorkSchedule } from '@/types/calendar';

import { ShiftAssignStatus } from '@/store/enums';
import { AM_PM_TIME_SPACE_UPPERCASE_FORMAT_NO_LEADING_ZERO, BLOCKS_IN_HOUR } from '@/config/variables';
import { CachedBlock } from '@/classes/cachedBlock';
import { getWeekIdByMillis } from '@/helpers/shiftTime';
import {
  getHourFromOffsetAndWeekId,
  getWeekdayFromOffsetAndWeekId,
  getWeekdayShortNameFromOffsetAndWeekId,
} from '@/helpers/contracts';
import { formatTimestamp, getWeekIdFromURL } from '@/helpers/date';
import { getTimezoneAbbreviation } from '@/helpers/timezone';

const NEXT_SCHEDULE_SPAN = 1;
const _checkIfScheduleComesAfter = (
  firstBlock: IScheduleBlock,
  secondBlock: IScheduleBlock,
  userTimezone: string,
): boolean => {
  const startAfterEachOther = secondBlock.startBlock === firstBlock.endBlock + NEXT_SCHEDULE_SPAN;

  const isAcceptedVto =
    firstBlock.shiftAssnStatus === ShiftAssignStatus.vtoAccepted ||
    secondBlock.shiftAssnStatus === ShiftAssignStatus.vtoAccepted;
  const dataSecondBlockStart = CachedBlock.fromBlockId(secondBlock.startBlock, userTimezone);
  const dataFirstBlockEnd = CachedBlock.fromBlockId(firstBlock.endBlock, userTimezone);
  const startOnTheSameDay = dataSecondBlockStart?.day === dataFirstBlockEnd?.day;

  return startAfterEachOther && startOnTheSameDay && !isAcceptedVto;
};

const groupSchedules = (shiftsArray: IScheduleBlock[], userTimezone: string): IScheduleBlock[][] => {
  // sort by start block
  const sortedByStartBlock = shiftsArray.sort((a, b) => a.startBlock - b.startBlock);

  const result = sortedByStartBlock.reduce<{
    total: IScheduleBlock[][];
    currentGroup: IScheduleBlock[];
    previousShift: IScheduleBlock | null;
  }>(
    (accumulator, currentShift, index, array) => {
      if (!accumulator.previousShift) {
        return {
          total: array.length !== 1 ? accumulator.total : [...accumulator.total, [currentShift]], // hold all groups of shifts
          currentGroup: [currentShift], // we push shifts here as long as they occure one after another
          previousShift: currentShift, // last shift used to compare if thet  occure one after another
        };
      }

      // check if current shift occures after the previous shift
      if (_checkIfScheduleComesAfter(accumulator.previousShift, currentShift, userTimezone)) {
        if (index === shiftsArray.length - 1)
          return {
            total: [...accumulator.total, [...accumulator.currentGroup, currentShift]],
            currentGroup: [],
            previousShift: null,
          };

        return {
          total: [...accumulator.total],
          currentGroup: [...accumulator.currentGroup, currentShift],
          previousShift: currentShift,
        };
      }

      // this shift doesn't occure after previous shift so create new array of grouped shifts
      if (index === shiftsArray.length - 1)
        return {
          total: [...accumulator.total, [...accumulator.currentGroup], [currentShift]],
          currentGroup: [],
          previousShift: null,
        };

      return {
        total: [...accumulator.total, [...accumulator.currentGroup]],
        currentGroup: [currentShift],
        previousShift: currentShift,
      };
    },
    { total: [], currentGroup: [], previousShift: null },
  );

  return result.total;
};

const getWeeklyHours = (schedule: ISchedule): number =>
  schedule.shifts.reduce<number>((shiftBlocks, shift) => {
    const shiftDuration = shift.endBlock + 1 - shift.startBlock;
    return shiftBlocks + shiftDuration;
  }, 0) / BLOCKS_IN_HOUR;

const getWorkSchedule = (
  interval: {
    startOffset: number;
    endOffset: number;
  },
  timeZone: string,
  clientTimezone?: string,
  displayTimezone?: boolean,
): IWorkSchedule => {
  const getHours = () => {
    const weekIdFromURL = getWeekIdFromURL() ?? getWeekIdByMillis(timeZone);
    const start = getHourFromOffsetAndWeekId(weekIdFromURL, interval.startOffset, timeZone, clientTimezone);
    const end = getHourFromOffsetAndWeekId(weekIdFromURL, interval.endOffset + 1, timeZone, clientTimezone);

    return `${formatTimestamp(start.unix, AM_PM_TIME_SPACE_UPPERCASE_FORMAT_NO_LEADING_ZERO)} - ${formatTimestamp(
      end.unix,
      AM_PM_TIME_SPACE_UPPERCASE_FORMAT_NO_LEADING_ZERO,
    )}${displayTimezone ? ` ${getTimezoneAbbreviation(timeZone)}` : ''}`;
  };

  return {
    hours: getHours(),
    weekday: getWeekdayFromOffsetAndWeekId(interval.startOffset),
    weekdayShort: getWeekdayShortNameFromOffsetAndWeekId(interval.startOffset),
  };
};

export { groupSchedules, getWeeklyHours, getWorkSchedule };
